import React, {Component} from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '../../../components/button';

/**
 * Mockup screens S6 ("Active — given" / "Active — received") and S7 (the dormant nomination card).
 *
 * BESIDE the incumbent ownership delegations, never merged with them. §1.2: one lends ownership of
 * a resource and the other lends (permission, scope) pairs under R3/R4/R5 — presenting them as one
 * list would tell the reader they are the same object, which is the false-equivalence defect the
 * audit panel's subject-id column already cost this programme once.
 */

const scopeText = (scope) =>
  (scope.type === 'global' ? 'everywhere' : `${scope.type} · ${scope.id}`);

const dateText = (value) => (value ? new Date(value).toLocaleDateString() : '—');

class AccessDelegations extends Component {
  renderPairs(pairs) {
    if (!pairs || !pairs.length) return <span className="muted">—</span>;
    return pairs.map((p, i) => (
      <div key={`${p.code}-${i}`} className="pair-line">
        <code>{p.code}</code> <span className="muted">{scopeText(p.scope)}</span>
      </div>
    ));
  }

  renderGiven() {
    const {given, onRevoke} = this.props;
    const live = given.filter((d) => !d.dormant);

    return (
      <div className="rounded-card" data-testid="ac-delegations-given">
        <h3>Active — given</h3>
        <Table size="small" className="data-table">
          <TableHead>
            <TableRow>
              <TableCell><strong>To</strong></TableCell>
              <TableCell><strong>Duty</strong></TableCell>
              <TableCell><strong>Until</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!live.length && (
              <TableRow>
                <TableCell colSpan={4} className="muted" data-testid="ac-given-empty">
                  You are not covering anyone else’s work out to anybody.
                </TableCell>
              </TableRow>
            )}
            {live.map((d) => (
              <TableRow key={d.delegationId} hover data-testid="ac-delegation-row">
                <TableCell>{d.delegateName || d.delegateSubjectId}</TableCell>
                <TableCell>{this.renderPairs(d.pairs)}</TableCell>
                <TableCell>{dateText(d.expiresAt)}</TableCell>
                <TableCell>
                  {/* §12.2: "You cannot extend this yourself once it is running — a second person
                      must." Ending it early is always yours, though: narrowing is monotone-safe. */}
                  <Button label="End now" className="btn btn-default"
                    onClick={() => onRevoke(d.delegationId)}
                    data-testid="ac-delegation-revoke" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  renderReceived() {
    const {received} = this.props;
    const live = received.filter((d) => !d.dormant);

    return (
      <div className="rounded-card" data-testid="ac-delegations-received">
        <h3>Active — received</h3>
        {!live.length ? (
          <div className="muted" data-testid="ac-received-empty">You are not covering for anyone.</div>
        ) : (
          <Table size="small" className="data-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Acting for</strong></TableCell>
                <TableCell><strong>Duty</strong></TableCell>
                <TableCell><strong>Until</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {live.map((d) => (
                <TableRow key={d.delegationId} hover data-testid="ac-received-row">
                  {/* Labelled "acting for X until Y" deliberately (§12.2): borrowed authority must
                      never read as the delegate's own, or the person exercising it cannot tell the
                      difference either. */}
                  <TableCell>
                    <strong>acting for {d.delegatorName || d.delegatorSubjectId}</strong>
                  </TableCell>
                  <TableCell>{this.renderPairs(d.pairs)}</TableCell>
                  <TableCell>{dateText(d.expiresAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }

  renderNomination() {
    const {given, onRevoke, onNominate, notProvisioned} = this.props;
    const dormant = given.filter((d) => d.dormant);

    return (
      <div className="rounded-card" data-testid="ac-nomination-card">
        <div className="card-head">
          <h3>Standing nomination</h3>
          <span className="muted">Set once, dormant until needed</span>
        </div>

        {!dormant.length ? (
          <div className="nomination-empty">
            <p className="muted" data-testid="ac-nomination-empty">
              You have not named a backup. A nomination sits dormant and grants nothing — it is only
              there so someone can turn cover on when you are unexpectedly away.
            </p>
            {this.renderNominateAction(onNominate, notProvisioned)}
          </div>
        ) : (
          dormant.map((d) => (
            <div key={d.delegationId} className="nomination-row" data-testid="ac-nomination-row">
              <div className="nomination-head">
                <span><strong>Backup</strong> {d.delegateName || d.delegateSubjectId}</span>
                <Chip size="small" label="Dormant — grants nothing" className="dormant-chip"
                  data-testid="ac-nomination-dormant" />
              </div>
              <div className="nomination-duties">{this.renderPairs(d.pairs)}</div>
              <div className="nomination-meta muted">
                Set on {dateText(d.grantedAt)} ·{' '}
                {/* Q7 SUPERSEDES THE MOCKUP, which drew "Benefits team lead". Decided 2026-09-13:
                    activation is a central act and platform_admin alone may perform it. */}
                Who can activate: a platform administrator
              </div>
              <div className="nomination-actions">
                {/* §7.4 — Edit is remove-and-recreate, not an in-place edit. A nomination is a
                    consent record; editing its duties after the fact would change what the absent
                    person agreed to under the original consent. */}
                <Button label="Remove" className="btn btn-default"
                  onClick={() => onRevoke(d.delegationId)} data-testid="ac-nomination-remove" />
              </div>
            </div>
          ))
        )}

        <div className="nomination-note">
          Dormant means dormant. The permissions exist in the record and confer nothing until
          activated — so naming a backup is not a way of quietly widening anyone’s access today.
        </div>
      </div>
    );
  }

  /**
   * The nominate/delegate action, which for most users is DISABLED with its reason.
   *
   * §3.6 grants the delegation capability per person, so "not provisioned" is the COMMON case, not
   * an edge. The empty-duty-list message would be the wrong thing to show here — it says "you hold
   * nothing delegatable", which is false of someone who owns a schema and simply has not been given
   * the capability.
   *
   * @param {function} onClick What to do when it is enabled.
   * @param {boolean} notProvisioned Whether the caller lacks `access.delegation:create`.
   * @return {object} The action element.
   */
  renderNominateAction(onClick, notProvisioned) {
    if (notProvisioned) {
      return (
        <Tooltip title="Delegating needs to be provisioned by an administrator">
          <span>
            <Button label="Name a backup" className="btn btn-default" disabled
              data-testid="ac-nominate-unprovisioned" />
          </span>
        </Tooltip>
      );
    }
    return <Button label="Name a backup" className="btn btn-default" onClick={onClick}
      data-testid="ac-nominate" />;
  }

  render() {
    const {onDelegate, notProvisioned} = this.props;

    return (
      <div className="access-delegations">
        <div className="section-head">
          <h2>Access delegations</h2>
          <p className="subtitle">
            Lend specific duties you hold to someone else for a bounded period. Separate from the
            ownership handoff above — this lends what you can <em>do</em>, not what you <em>own</em>.
          </p>
          {notProvisioned ? (
            <Tooltip title="Delegating needs to be provisioned by an administrator">
              <span>
                <Button label="Delegate duties" className="btn btn-action" disabled
                  data-testid="ac-delegate-unprovisioned" />
              </span>
            </Tooltip>
          ) : (
            <Button label="Delegate duties" className="btn btn-action" onClick={onDelegate}
              data-testid="ac-delegate-open" />
          )}
        </div>

        {this.renderGiven()}
        {this.renderReceived()}
        {this.renderNomination()}
      </div>
    );
  }
}

export default AccessDelegations;
